import React, { useState, useEffect } from 'react'; // Import useEffect
import { Upload, List, Button, message, Modal, Progress, Spin } from 'antd';
import { UploadOutlined, PlayCircleOutlined, DeleteOutlined, CloseSquareOutlined } from '@ant-design/icons';
import axiosInstance from '../../utils/axiosInstance';

const formatSize = (size) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const VideoManager = ({ gameId }) => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false); // Added loading state

  const loadVideos = async () => {
    setLoading(true); // Start loading
    try {
      const response = await axiosInstance.get(`api/list-videos/${gameId}/`);
      const videoData = await Promise.all(
        response.data.videos.map(async (video) => {
          const presignedResponse = await axiosInstance.get(
            `api/generate-presigned-url/${gameId}/`, 
            { params: { file_name: video.name, operation: 'get_object' } }
          );
          return { ...video, url: presignedResponse.data.url }; // Use the playback URL
        })
      );
      setVideos(videoData);
    } catch (error) {
      console.error('Error loading videos:', error);
      message.error('Failed to load videos.');
    } finally {
      setLoading(false); // End loading
    }
  };

  // Automatically load videos when component mounts
  useEffect(() => {
    loadVideos(); // Call loadVideos when the component mounts
  }, [gameId]); // Reload videos if gameId changes

  const handleUpload = async ({ file }) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await axiosInstance.get(`api/generate-presigned-url/${gameId}/`, {
        params: { file_name: file.name, file_type: file.type, operation: 'put_object' },
      });

      const { url } = response.data;

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(percentCompleted);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          message.success('Video uploaded successfully!');
          loadVideos(); // Reload videos after upload
        } else {
          message.error('Failed to upload video.');
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        message.error('Failed to upload video.');
        setUploading(false);
      };

      xhr.send(file);
    } catch (error) {
      console.error('Error uploading video:', error);
      message.error('Failed to upload video.');
      setUploading(false);
    }
  };

  const handleDelete = (video) => {
    Modal.confirm({
      title: 'Confirm Video Deletion',
      content: (
        <div>
          <p>Are you sure you want to delete this video?</p>
          <p>
            <strong>{video.name}</strong>
          </p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axiosInstance.delete(`api/delete-video/${gameId}/${video.name}/`);
          message.success('Video deleted successfully!');
          loadVideos(); // Reload videos after deletion
        } catch (error) {
          console.error('Error deleting video:', error);
          message.error('Failed to delete video.');
        }
      },
    });
  };

  return (
    <div>
      {loading ? ( // Display loading spinner during API calls
        <Spin/>
      ) : (
        <>
      <Upload customRequest={handleUpload} multiple showUploadList={false}>
        <Button icon={<UploadOutlined />}>Upload Videos</Button>
      </Upload>

      {uploading && (
        <div style={{ marginTop: '20px' }}>
          <Progress percent={uploadProgress} status={uploadProgress === 100 ? 'success' : 'active'} />
        </div>
      )}

      <List
        bordered
        dataSource={videos}
        renderItem={(video) => (
          <List.Item
            actions={[
              <Button
                type="link"
                icon={<PlayCircleOutlined />}
                onClick={() => setSelectedVideo(video.url)}
              />,
              <Button
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(video)}
                danger
              />,
            ]}
          >
            <div>
              <strong>{video.name}</strong> ({formatSize(video.size)})
            </div>
          </List.Item>
        )}
        style={{ marginTop: '20px' }}
      />

      {selectedVideo && (
        <div style={{ marginTop: '20px', position: 'relative' }}>
          <video
            src={selectedVideo} // Use the pre-signed URL for playback
            controls
            style={{ width: '100%', maxHeight: '400px' }}
          />
          <Button
            icon={<CloseSquareOutlined />}
            onClick={() => setSelectedVideo(null)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              background: 'rgba(255, 255, 255, 0.8)',
              border: '1px solid #ccc',
            }}
          >
            Close
          </Button>
        </div>
      )}
      </>
    )}
  </div>
);
};

export default VideoManager;
